import { authStore } from "@kaplan-labs/up-auth-api-client";

type BrazeSdk = typeof import("@braze/web-sdk");
type BrazeState = "idle" | "loading" | "active";

let state: BrazeState = "idle";
let sdk: BrazeSdk | undefined;
let authUnsubscribe: (() => void) | undefined;

// Braze persists identifiers (ab.storage.deviceId.<apiKey>,
// ab.storage.sessionId.<apiKey>, ...) in cookies and localStorage, suffixed
// with the API key.
const BRAZE_STORAGE_PREFIX = "ab.storage.";

function getApiKey(): string | undefined {
  return process.env.NEXT_PUBLIC_BRAZE_API_KEY || undefined;
}

function isClient(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function identifyCurrentUser(braze: BrazeSdk): void {
  const userId = authStore.get().currentUser?.id;
  if (userId) {
    braze.changeUser(userId);
  } else {
    braze.openSession();
  }
}

/**
 * Initialize Braze for the current visitor. Call only once consent for Braze's
 * OneTrust category has been granted; safe to call repeatedly. Nothing Braze
 * related (SDK bundle, cookies, localStorage, network) runs before this.
 */
export function brazeEnable(): void {
  if (!isClient() || state !== "idle") return;

  const apiKey = getApiKey();
  if (!apiKey) return;

  state = "loading";

  import("@braze/web-sdk")
    .then((braze) => {
      // Consent was withdrawn (or re-granted and already handled by another
      // in-flight load) while the bundle was downloading.
      if (state !== "loading") return;

      const isInitialized = braze.initialize(apiKey, {
        baseUrl: process.env.NEXT_PUBLIC_BRAZE_SDK_URL as string,
        allowUserSuppliedJavascript: true,
      });

      if (!isInitialized) {
        state = "idle";
        return;
      }

      sdk = braze;
      state = "active";

      identifyCurrentUser(braze);
      braze.automaticallyShowInAppMessages();

      authUnsubscribe = authStore.listen(() => {
        identifyCurrentUser(braze);
      });
    })
    .catch(() => {
      if (state === "loading") state = "idle";
    });
}

/**
 * Stop Braze and remove everything it stored for this visitor. Call when
 * consent for Braze's OneTrust category is denied or withdrawn; safe to call
 * repeatedly and when Braze was never enabled. Also purges Braze storage left
 * behind by an earlier page view (e.g. from before consent was withdrawn).
 */
export function brazeDisable(): void {
  if (!isClient()) return;

  if (authUnsubscribe) {
    authUnsubscribe();
    authUnsubscribe = undefined;
  }

  if (state === "active" && sdk) {
    // wipeData() needs a live instance, so it must run before destroy().
    sdk.wipeData();
    sdk.destroy();
  }

  sdk = undefined;
  state = "idle";

  purgeBrazeStorage();
}

function isBrazeKey(name: string, apiKey: string): boolean {
  return name.startsWith(BRAZE_STORAGE_PREFIX) && name.endsWith(`.${apiKey}`);
}

// Braze writes its cookies on the broadest domain it is allowed to (e.g.
// `.harvard.edu` for pc.hms.harvard.edu), so expire each one on every
// candidate domain. Only keys for this site's API key are touched, so Braze
// cookies belonging to other properties on a shared parent domain survive.
function purgeBrazeStorage(): void {
  const apiKey = getApiKey();
  if (!apiKey) return;

  const hostParts = window.location.hostname.split(".");
  const domains = [
    undefined,
    ...hostParts.map((_, index) => `.${hostParts.slice(index).join(".")}`),
  ];
  const expired = "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";

  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0]?.trim();
    if (!name || !isBrazeKey(name, apiKey)) continue;

    for (const domain of domains) {
      document.cookie = domain
        ? `${name}${expired};domain=${domain}`
        : `${name}${expired}`;
    }
  }

  try {
    for (const key of Object.keys(window.localStorage)) {
      if (isBrazeKey(key, apiKey)) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage can throw when storage is blocked; nothing to purge then.
  }
}
